import React, { Component } from 'react';
import hoistStatics from 'hoist-non-react-statics';
import * as logger from 'universal-logger';

/**
 * Decorator that allows you to render a fallback component if the decorated component
 * throws an error during its render.
 *
 * It takes a single configuration option, `renderFallback`, which gets called with
 * all of the original props passed to the decorated component, and two additional props;
 * `handleResetError` which can be called if you want to have the boundary try to
 * re-render the original component and `error` which is the original error that was
 * thrown in case specific logging wants to be done.
 * `handleResetError` should only be called in cases when there's a known
 * way of resetting or fixing the failing state in the original component (otherwise, you risk
 * creating render loops).
 *
 * The boundary will reset from its errored state automatically when it's unmounted
 * (for example, when the user navigates away or refreshes the page).
 *
 * @example
 *  // Assume that text/setText is some external state.
 *
 *  // Our soon-to-be-decorated component
 *  const OxfordComma = ({ text }) => (
 *    <div>
 *      {text.split(' and').join(', and')}
 *    </div>
 *  );
 *
 *  // Our fallback component
 *  const OxfordCommaFallback = ({ acknowledgeMessup, setText }) => (
 *    <div>
 *      <button onClick={() => {
 *        setText('');
 *        acknowledgeMessup();
 *      }}>
 *        I messed up
 *      </button>
 *    </div>
 *  );
 *
 *  // Our component container
 *  const renderFallback = ({ handleResetError, setText }) => (
 *    <OxfordCommaFallback
 *      acknowledgeMessup={handleResetError}
 *      setText={setText}
 *    />
 *  );
 *
 *  export const OxfordCommaContainer = fallbackBoundary({ renderFallback })(OxfordComma);
 *
 *  // Given
 *  <OxfordCommaContainer text={null} />
 *  // Will render the fallback, since null.split causes a TypeError.
 *  // Once the button is clicked, the decorated component renders again.
 */
export const fallbackBoundary = ({ renderFallback }) => WrappedComponent => {
  if (typeof renderFallback !== 'function') {
    throw new TypeError('FallbackBoundary configuration error: renderFallback must be a function.');
  }

  const originalName = WrappedComponent.displayName || WrappedComponent.name;

  class FallbackBoundary extends Component {
    constructor(...args) {
      super(...args);
      this.handleResetError = this.handleResetError.bind(this);
      this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
      return { hasError: true, error };
    }

    componentDidCatch(error) {
      logger.warn(`FallbackBoundary Error for ${originalName}`, error);
    }

    handleResetError() {
      this.setState({ hasError: false, error: null });
    }

    render() {
      const { error, hasError } = this.state;

      if (hasError) {
        return renderFallback({
          ...this.props,
          error,
          handleResetError: this.handleResetError,
        });
      }

      return <WrappedComponent {...this.props} />;
    }
  }

  FallbackBoundary.displayName = `fallbackBoundary(${originalName})`;

  return hoistStatics(FallbackBoundary, WrappedComponent);
};
